import "../../global.css";

import React, { useEffect, useLayoutEffect, useState } from "react";
import { Appearance, I18nManager, Platform } from "react-native";
import {
  ErrorBoundaryProps,
  Slot,
  useNavigationContainerRef,
  useSegments,
} from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import * as WebBrowser from "expo-web-browser";
import { PortalHost } from "@rn-primitives/portal";
import * as Sentry from "@sentry/react-native";
import { QueryClientProvider } from "@tanstack/react-query";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { SafeAreaProvider } from "react-native-safe-area-context";

import { ErrorFallback } from "@/blocks/ErrorFallback";
import { VnToaster } from "@/components/VnToaster";
import { ThemeProvider } from "@/context/ThemeProvider";
import { useLoadWebFonts } from "@/hooks/useLoadWebFonts";
import { i18n } from "@/i18n";
import { initializeFirebaseApp } from "@/services/firebase";
import mixpanel, { initMixpanel } from "@/services/mixpanel";
import { initSentry, navigationIntegration } from "@/services/sentry";
import { setupAuthListener } from "@/stores/authStore";
import { setAndroidNavigationBar } from "@/utils/android";
import { queryClient } from "@/utils/queryClient";
import { setupSvgStyling } from "@/utils/svg";

WebBrowser.maybeCompleteAuthSession();

// Prevent the splash screen from auto-hiding before asset loading is complete.
void SplashScreen.preventAutoHideAsync();
// Set the animation options. This is optional.
SplashScreen.setOptions({
  duration: 500,
  fade: true,
});

// Sentry Initialization
initSentry();

// Firebase Initialization
initializeFirebaseApp();

// Mixpanel Initialization
initMixpanel();

setupSvgStyling();

I18nManager.allowRTL(i18n.language === "ar");
I18nManager.forceRTL(i18n.language === "ar");

const useSetAndroidNavigationBar = () => {
  useLayoutEffect(() => {
    void setAndroidNavigationBar(Appearance.getColorScheme() ?? "light");
  }, []);
};

const usePlatformSpecificSetup = Platform.select({
  android: useSetAndroidNavigationBar,
  web: useLoadWebFonts,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  default: () => {},
});

function RootLayout() {
  const ref = useNavigationContainerRef();
  const segments = useSegments();
  const route = `/${segments.join("/")}`;
  usePlatformSpecificSetup();

  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    if (!isAppReady) {
      return;
    }
    mixpanel.track("Screen Viewed", { route });
  }, [route, isAppReady]);

  useEffect(() => {
    if (ref) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  useEffect(() => {
    const authSubscriber = setupAuthListener();
    return authSubscriber;
  }, []);

  useEffect(() => {
    const initializeApp = () => {
      if (isAppReady) {
        return;
      }

      setIsAppReady(true);
    };

    void initializeApp();
  }, [isAppReady]);

  useEffect(() => {
    if (isAppReady) {
      void SplashScreen.hideAsync();
    }
  }, [isAppReady]);

  return (
    <ThemeProvider>
      <GestureHandlerRootView>
        <SafeAreaProvider>
          <KeyboardProvider>
            <QueryClientProvider client={queryClient}>
              <Slot />
              <PortalHost />
              <VnToaster />
            </QueryClientProvider>
          </KeyboardProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </ThemeProvider>
  );
}

// Catch any errors in the root layout and display a fallback UI
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  return <ErrorFallback error={error} resetError={retry} />;
}

export default Sentry.wrap(RootLayout);
